import { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, Trash2, Check, AlertTriangle, Upload, Sparkles } from '../lib/icons';
import { toast } from 'sonner';
import {
  fetchFinancements,
  creerContratFinancement,
  supprimerContratFinancement,
  marquerEcheanceFinancement,
  definirPlafondCampagne,
  enregistrerDeblocage,
  rembourserDeblocage,
  fetchEcheancierGlobal,
  analyserAmortissement,
  analyserRelevesMultiBanques,
} from '../lib/dataProvider';
import { useAuth } from '../lib/auth';
import { deposerDocument } from '../lib/docs';
import { fmt, PageTitle, StatBlock } from '../components/shared/badges';
import { PrevisionnelCanonique } from '../components/canonique/BlocsCanoniques';

const inputCls =
  'mt-1.5 h-11 w-full border border-slate-300 bg-slate-50 px-3 font-data text-sm text-slate-900 outline-none transition-colors duration-150 focus:border-slate-900 focus:bg-white';
const label = 'text-xs font-bold uppercase tracking-[0.1em] text-slate-500';
const th = 'px-3 py-2 text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500';
const td = 'px-3 py-2 text-sm text-slate-800';

const ONGLETS = [
  { id: 'financements', label: 'Crédits & Leasings', hint: 'Contrats et tableaux d’amortissement' },
  { id: 'campagne', label: 'Crédit de campagne', hint: 'Plafond, déblocages et agios' },
  { id: 'echeancier', label: 'Échéancier global', hint: 'Toutes les sorties par semaine' },
  { id: 'canonique', label: 'Prévisionnel canonique', hint: 'Échéances et financements du serveur' },
];

const COULEUR_SOURCE = {
  Fournisseur: 'border-sky-300 bg-sky-50 text-sky-800',
  Leasing: 'border-violet-300 bg-violet-50 text-violet-800',
  Crédit: 'border-amber-300 bg-amber-50 text-amber-900',
  'Crédit campagne': 'border-emerald-300 bg-emerald-50 text-emerald-800',
};

export default function Tresorerie() {
  const aujourdhui = new Date().toISOString().slice(0, 10);
  const { user } = useAuth();
  const [onglet, setOnglet] = useState('financements');
  const [data, setData] = useState({ contrats: [], campagne: { plafond: 0, deblocages: [], encours: 0, disponible: 0, agiosCumules: 0 } });
  const [echeancier, setEcheancier] = useState([]);
  const [detail, setDetail] = useState(null);
  const [chargement, setChargement] = useState('');
  const [contrat, setContrat] = useState({ libelle: '', organisme: '', type: 'Leasing', montantFinance: '', taux: '', lignes: [] });
  const [plafond, setPlafond] = useState({ plafond: '', banque: '' });
  const [detections, setDetections] = useState([]);
  const [complement, setComplement] = useState(null);

  const load = useCallback(async () => {
    const d = await fetchFinancements();
    setData(d);
    setPlafond((p) => ({ plafond: p.plafond || d.campagne.plafond || '', banque: p.banque || d.campagne.banque || '' }));
    setEcheancier(await fetchEcheancierGlobal());
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const importerAmortissement = async (file) => {
    if (!file) return;
    setChargement('amortissement');
    // Le contrat/tableau est archivé au coffre-fort documentaire.
    deposerDocument(file, { source: 'amortissement', categorie: 'Contrat & financement', deposePar: user?.name, indexer: false });
    const { error, lignes, contrat: infos } = await analyserAmortissement(file);
    setChargement('');
    if (error) return toast.error(error);
    setContrat((c) => ({
      ...c,
      libelle: c.libelle || infos?.libelle || '',
      organisme: c.organisme || infos?.organisme || '',
      montantFinance: c.montantFinance || infos?.montantFinance || '',
      taux: c.taux || infos?.taux || '',
      lignes: lignes ?? [],
    }));
    toast.success(`${(lignes ?? []).length} échéance(s) extraites — vérifiez puis validez`);
  };

  const enregistrerContrat = async () => {
    const { error, contrat: cree } = await creerContratFinancement({ ...contrat, utilisateur: user?.name });
    if (error) return toast.error(error);
    toast.success(`${cree.type} « ${cree.libelle} » enregistré — ${cree.lignes.length} échéances dans l'échéancier`);
    setContrat({ libelle: '', organisme: '', type: 'Leasing', montantFinance: '', taux: '', lignes: [] });
    load();
  };

  const importerReleve = async (files) => {
    const liste = Array.from(files ?? []);
    if (!liste.length) return;
    setChargement('releve');
    const resultats = await analyserRelevesMultiBanques(liste);
    setChargement('');
    const erreurs = resultats.filter((r) => r.error);
    erreurs.forEach((r) => toast.error(`${r.fichier} : ${r.error}`));
    const lignes = resultats.flatMap((r) => r.deblocages);
    setDetections(lignes);
    const nouveaux = lignes.filter((d) => !d.rapproche).length;
    const rapproches = lignes.length - nouveaux;
    toast[nouveaux ? 'success' : 'info'](
      lignes.length
        ? `${liste.length} extrait(s) analysé(s) — ${nouveaux} nouveau(x) déblocage(s), ${rapproches} déjà rapproché(s)`
        : 'Aucun déblocage de crédit détecté sur ces extraits'
    );
  };

  const totalParSemaine = useMemo(() => {
    const m = {};
    echeancier.forEach((l) => {
      m[l.semaine] = (m[l.semaine] || 0) + l.montant;
    });
    return m;
  }, [echeancier]);
  const semaineMax = Math.max(1, ...Object.values(totalParSemaine));
  const total30j = echeancier
    .filter((l) => l.echeance <= new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10))
    .reduce((s, l) => s + l.montant, 0);

  return (
    <div data-testid="tresorerie-page" className="space-y-6">
      <PageTitle
        emoji="🏦"
        titre="Trésorerie & Financements"
        sous="Crédits, leasings, crédit de campagne et échéancier global — lecture des documents assistée par IA"
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatBlock testid="stat-contrats" titre="Contrats de financement" accent="border-l-violet-600" items={[['Contrats', fmt(data.contrats.length)], ['Reste dû TTC', fmt(data.contrats.reduce((s, c) => s + c.resteTTC, 0), 3)]]} />
        <StatBlock testid="stat-plafond" titre="Crédit de campagne" accent="border-l-emerald-600" items={[['Plafond', fmt(data.campagne.plafond, 3)], ['Disponible', fmt(data.campagne.disponible, 3)]]} />
        <StatBlock testid="stat-agios" titre="Agios cumulés" accent="border-l-red-600" items={[['Coût du crédit (TND)', fmt(data.campagne.agiosCumules, 3)]]} />
        <StatBlock testid="stat-30j" titre="Sorties à 30 jours" accent="border-l-slate-900" items={[['Échéances', fmt(echeancier.filter((l) => l.echeance <= new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10)).length)], ['Montant (TND)', fmt(total30j, 3)]]} />
      </div>

      <div data-testid="onglets-tresorerie" className="flex flex-wrap gap-px bg-slate-200">
        {ONGLETS.map((o) => (
          <button
            key={o.id}
            type="button"
            data-testid={`onglet-${o.id}`}
            onClick={() => setOnglet(o.id)}
            className={`flex-1 px-4 py-2.5 text-left transition-colors duration-150 ${onglet === o.id ? 'bg-slate-900 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`}
          >
            <span className="block text-sm font-bold">{o.label}</span>
            <span className={`text-xs ${onglet === o.id ? 'text-slate-300' : 'text-slate-500'}`}>{o.hint}</span>
          </button>
        ))}
      </div>

      {onglet === 'financements' && (
        <>
          <section data-testid="form-contrat" className="border border-slate-200 bg-white">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-4 py-3">
              <p className="font-heading text-base font-bold text-slate-900">Nouveau contrat (crédit ou leasing)</p>
              <label
                data-testid="import-amortissement-label"
                className={`flex h-11 cursor-pointer items-center gap-2 border-2 border-slate-900 px-3 font-heading text-sm font-bold ${chargement === 'amortissement' ? 'bg-slate-300 text-slate-600' : 'bg-slate-900 text-white hover:bg-slate-700'}`}
              >
                {chargement === 'amortissement' ? <Sparkles size={16} className="animate-pulse" /> : <Upload size={16} />}
                {chargement === 'amortissement' ? 'Lecture IA en cours…' : 'Importer le tableau d’amortissement (PDF)'}
                <input data-testid="import-amortissement" type="file" accept=".pdf,.xlsx,.csv" className="hidden" disabled={chargement === 'amortissement'} onChange={(e) => importerAmortissement(e.target.files?.[0])} />
              </label>
            </div>
            <div className="grid grid-cols-1 gap-4 px-4 py-4 md:grid-cols-2 xl:grid-cols-5">
              <div>
                <label className={label}>Libellé *</label>
                <input data-testid="contrat-libelle" value={contrat.libelle} onChange={(e) => setContrat({ ...contrat, libelle: e.target.value })} className={inputCls} placeholder="ex. Leasing chargeur" />
              </div>
              <div>
                <label className={label}>Organisme / banque</label>
                <input data-testid="contrat-organisme" value={contrat.organisme} onChange={(e) => setContrat({ ...contrat, organisme: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className={label}>Nature</label>
                <select data-testid="contrat-type" value={contrat.type} onChange={(e) => setContrat({ ...contrat, type: e.target.value })} className={inputCls}>
                  <option value="Leasing">Leasing</option>
                  <option value="Crédit">Crédit</option>
                </select>
              </div>
              <div>
                <label className={label}>Montant financé (TND)</label>
                <input data-testid="contrat-montant" type="number" step="0.001" min="0" value={contrat.montantFinance} onChange={(e) => setContrat({ ...contrat, montantFinance: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className={label}>Taux (%)</label>
                <input data-testid="contrat-taux" type="number" step="0.01" min="0" value={contrat.taux} onChange={(e) => setContrat({ ...contrat, taux: e.target.value })} className={inputCls} />
              </div>
            </div>

            {contrat.lignes.length > 0 && (
              <div data-testid="apercu-amortissement" className="border-t border-slate-200">
                <p className="px-4 py-2 text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">
                  Contrôle avant validation — {contrat.lignes.length} échéance(s) extraites · total TTC{' '}
                  {fmt(contrat.lignes.reduce((s, l) => s + (Number(l.ttc) || 0), 0), 3)} TND
                </p>
                <div className="max-h-72 overflow-y-auto">
                  <table className="w-full border-collapse text-left">
                    <thead className="bg-slate-50">
                      <tr>{['#', 'Échéance', 'Capital amorti', 'Intérêts', 'TVA', 'Montant TTC', ''].map((c) => <th key={c} className={th}>{c}</th>)}</tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {contrat.lignes.map((l, i) => (
                        <tr key={i} data-testid={`ligne-amort-${i}`}>
                          <td className={`${td} font-data text-xs text-slate-500`}>{i + 1}</td>
                          {['echeance', 'capital', 'interets', 'tva', 'ttc'].map((k) => (
                            <td key={k} className="px-2 py-1">
                              <input
                                data-testid={`amort-${k}-${i}`}
                                type={k === 'echeance' ? 'date' : 'number'}
                                step="0.001"
                                value={l[k] ?? ''}
                                onChange={(e) =>
                                  setContrat((c) => ({ ...c, lignes: c.lignes.map((x, j) => (j === i ? { ...x, [k]: e.target.value } : x)) }))
                                }
                                className="h-9 w-full border border-slate-200 bg-white px-2 text-right font-data text-xs outline-none focus:border-slate-900"
                              />
                            </td>
                          ))}
                          <td className="px-2 py-1 text-right">
                            <button type="button" data-testid={`amort-retirer-${i}`} onClick={() => setContrat((c) => ({ ...c, lignes: c.lignes.filter((_, j) => j !== i) }))} className="border border-slate-300 p-1.5 text-slate-500 hover:bg-red-600 hover:text-white">
                              <Trash2 size={12} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="border-t border-slate-200 px-4 py-4">
              <button
                type="button"
                data-testid="contrat-valider"
                disabled={!contrat.libelle || contrat.lignes.length === 0}
                onClick={enregistrerContrat}
                className={`flex h-12 w-full items-center justify-center gap-2 font-heading text-sm font-bold uppercase tracking-[0.06em] text-white ${!contrat.libelle || contrat.lignes.length === 0 ? 'cursor-not-allowed bg-slate-300' : 'bg-emerald-700 hover:bg-emerald-800'}`}
              >
                <Plus size={16} /> Valider le contrat et injecter les échéances
              </button>
            </div>
          </section>

          <section className="border border-slate-200 bg-white">
            <p className="border-b border-slate-200 px-4 py-3 font-heading text-base font-bold text-slate-900">
              Contrats en cours <span className="font-data text-xs text-slate-500">({data.contrats.length})</span>
            </p>
            <table className="w-full border-collapse text-left" data-testid="table-contrats">
              <thead className="bg-slate-50">
                <tr>{['Réf.', 'Libellé', 'Organisme', 'Nature', 'Financé', 'Taux', 'Échéances', 'Total TTC', 'Reste dû', ''].map((c) => <th key={c} className={th}>{c}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.contrats.length === 0 && <tr><td colSpan={10} className="px-3 py-10 text-center text-sm text-slate-500">Aucun contrat enregistré.</td></tr>}
                {data.contrats.map((c) => (
                  <tr key={c.id} data-testid={`contrat-${c.id}`} className="hover:bg-slate-50">
                    <td className={`${td} font-data text-xs font-semibold`}>{c.numero}</td>
                    <td className={`${td} font-semibold`}>{c.libelle}</td>
                    <td className={td}>{c.organisme || '—'}</td>
                    <td className={`${td} text-xs`}>{c.type}</td>
                    <td className={`${td} tabular font-data text-right`}>{fmt(c.montantFinance, 3)}</td>
                    <td className={`${td} font-data text-right text-xs`}>{c.taux ? `${fmt(c.taux, 2)} %` : '—'}</td>
                    <td className={`${td} font-data text-xs`}>{c.nbPayees}/{c.nbEcheances} payées</td>
                    <td className={`${td} tabular font-data text-right`}>{fmt(c.totalTTC, 3)}</td>
                    <td className={`${td} tabular font-data text-right font-bold`}>{fmt(c.resteTTC, 3)}</td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex justify-end gap-1.5">
                        <button type="button" data-testid={`detail-contrat-${c.id}`} onClick={() => setDetail(detail === c.id ? null : c.id)} className="border border-slate-300 px-2 py-1 text-xs font-bold text-slate-700 hover:bg-slate-900 hover:text-white">
                          Échéances
                        </button>
                        <button type="button" data-testid={`supprimer-contrat-${c.id}`} onClick={async () => { if (window.confirm(`Supprimer « ${c.libelle} » et ses échéances ?`)) { await supprimerContratFinancement(c.id); toast.success('Contrat supprimé'); load(); } }} className="border border-slate-300 p-1.5 text-slate-600 hover:bg-red-600 hover:text-white">
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {detail && (
              <div data-testid={`echeances-contrat-${detail}`} className="border-t-2 border-slate-900">
                <table className="w-full border-collapse text-left">
                  <thead className="bg-slate-50">
                    <tr>{['Échéance', 'Capital', 'Intérêts', 'TVA', 'TTC', 'Statut', ''].map((c) => <th key={c} className={th}>{c}</th>)}</tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {data.contrats.find((c) => c.id === detail)?.lignes.map((l) => (
                      <tr key={l.id} className={l.paye ? 'text-slate-400' : ''}>
                        <td className={`${td} font-data text-xs font-semibold`}>{l.echeance}</td>
                        <td className={`${td} tabular font-data text-right text-xs`}>{fmt(l.capital, 3)}</td>
                        <td className={`${td} tabular font-data text-right text-xs`}>{fmt(l.interets, 3)}</td>
                        <td className={`${td} tabular font-data text-right text-xs`}>{fmt(l.tva, 3)}</td>
                        <td className={`${td} tabular font-data text-right font-semibold`}>{fmt(l.ttc, 3)}</td>
                        <td className={`${td} text-xs`}>{l.paye ? `Payée le ${l.payeLe}` : 'À payer'}</td>
                        <td className="px-3 py-2 text-right">
                          <button type="button" data-testid={`payer-${detail}-${l.id}`} onClick={async () => { await marquerEcheanceFinancement(detail, l.id, user?.name); load(); }} className="border border-slate-300 px-2 py-1 text-xs font-bold text-slate-700 hover:bg-slate-900 hover:text-white">
                            {l.paye ? 'Annuler' : 'Marquer payée'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}

      {onglet === 'campagne' && (
        <>
          <section data-testid="plafond-campagne" className="border border-slate-200 bg-white">
            <p className="border-b border-slate-200 px-4 py-3 font-heading text-base font-bold text-slate-900">Enveloppe de campagne accordée par la banque</p>
            <div className="grid grid-cols-1 gap-4 px-4 py-4 sm:grid-cols-4">
              <div>
                <label className={label}>Plafond accordé (TND)</label>
                <input data-testid="campagne-plafond" type="number" step="0.001" min="0" value={plafond.plafond} onChange={(e) => setPlafond({ ...plafond, plafond: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className={label}>Banque</label>
                <input data-testid="campagne-banque" value={plafond.banque} onChange={(e) => setPlafond({ ...plafond, banque: e.target.value })} className={inputCls} placeholder="ex. Attijari" />
              </div>
              <div className="sm:col-span-2 flex items-end gap-3">
                <button type="button" data-testid="campagne-enregistrer" onClick={async () => { const { error } = await definirPlafondCampagne(plafond); if (error) return toast.error(error); toast.success('Plafond de campagne enregistré'); load(); }} className="h-11 bg-slate-900 px-4 font-heading text-sm font-bold uppercase tracking-[0.06em] text-white hover:bg-slate-700">
                  Enregistrer le plafond
                </button>
                <p data-testid="campagne-disponible" className="font-data text-sm text-slate-700">
                  Encours : <span className="font-bold">{fmt(data.campagne.encours, 3)}</span> · Reste disponible :{' '}
                  <span className="text-lg font-bold text-emerald-700">{fmt(data.campagne.disponible, 3)} TND</span>
                </p>
              </div>
            </div>
            <div className="border-t border-slate-200 px-4 py-3">
              <label
                data-testid="import-releve-label"
                className={`flex h-11 w-full cursor-pointer items-center justify-center gap-2 border-2 border-slate-900 px-3 font-heading text-sm font-bold ${chargement === 'releve' ? 'bg-slate-300 text-slate-600' : 'bg-white text-slate-900 hover:bg-slate-900 hover:text-white'}`}
              >
                {chargement === 'releve' ? <Sparkles size={16} className="animate-pulse" /> : <Upload size={16} />}
                {chargement === 'releve' ? 'Analyse IA des extraits…' : 'Importer des extraits bancaires (multi-banques : Attijari, BIAT… — PDF / Excel)'}
                <input data-testid="import-releve" type="file" multiple accept=".pdf,.xlsx,.xls,.csv" className="hidden" disabled={chargement === 'releve'} onChange={(e) => importerReleve(e.target.files)} />
              </label>
            </div>
            {detections.length > 0 && (
              <div data-testid="detections-deblocage" className="border-t-2 border-amber-400 bg-amber-50/60 px-4 py-3">
                <p className="font-heading text-sm font-bold text-amber-900">
                  {detections.filter((d) => !d.rapproche).length} déblocage(s) à compléter ·{' '}
                  {detections.filter((d) => d.rapproche).length} déjà rapproché(s) automatiquement
                </p>
                {detections.map((d, i) => (
                  <div key={i} data-testid={`detection-${i}`} className={`mt-2 flex flex-wrap items-center justify-between gap-2 border px-3 py-2 ${d.rapproche ? 'border-slate-200 bg-slate-50 text-slate-400' : 'border-amber-300 bg-white'}`}>
                    <p className="font-data text-xs text-slate-700">
                      {d.banque && <span className="mr-2 border border-slate-300 bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold uppercase">{d.banque}</span>}
                      {d.date} · {d.libelle} · <span className="text-sm font-bold text-slate-900">montant net {fmt(d.montantNet, 3)} TND</span>
                      {d.rapproche && <span data-testid={`rapproche-${i}`} className="ml-2 border border-emerald-300 bg-emerald-50 px-1.5 py-0.5 text-[10px] font-bold uppercase text-emerald-700">Déjà enregistré</span>}
                    </p>
                    <div className="flex gap-2">
                      {!d.rapproche && (
                        <button type="button" data-testid={`completer-detection-${i}`} onClick={() => setComplement({ ...d, nominal: '', echeance: '', index: i })} className="h-9 bg-slate-900 px-3 text-xs font-bold text-white hover:bg-slate-700">
                          Compléter
                        </button>
                      )}
                      <button type="button" data-testid={`ignorer-detection-${i}`} onClick={() => setDetections((ds) => ds.filter((_, j) => j !== i))} className="h-9 border border-slate-300 px-3 text-xs font-semibold text-slate-600 hover:bg-slate-100">
                        Ignorer
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="border border-slate-200 bg-white">
            <p className="border-b border-slate-200 px-4 py-3 font-heading text-base font-bold text-slate-900">
              Déblocages du crédit de campagne <span className="font-data text-xs text-slate-500">({data.campagne.deblocages.length})</span>
            </p>
            <table className="w-full border-collapse text-left" data-testid="table-deblocages">
              <thead className="bg-slate-50">
                <tr>{['Réf.', 'Date', 'Libellé', 'Montant net encaissé', 'Montant nominal (dette)', 'Agios', 'Échéance traite', 'Statut', ''].map((c) => <th key={c} className={th}>{c}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.campagne.deblocages.length === 0 && <tr><td colSpan={9} className="px-3 py-10 text-center text-sm text-slate-500">Aucun déblocage enregistré.</td></tr>}
                {data.campagne.deblocages.map((d) => (
                  <tr key={d.id} data-testid={`deblocage-${d.id}`} className={d.rembourse ? 'text-slate-400' : ''}>
                    <td className={`${td} font-data text-xs font-semibold`}>{d.numero}</td>
                    <td className={`${td} font-data text-xs`}>{d.date}</td>
                    <td className={`${td} text-xs`}>{d.libelle}</td>
                    <td className={`${td} tabular font-data text-right`}>{fmt(d.montantNet, 3)}</td>
                    <td className={`${td} tabular font-data text-right font-semibold`}>{fmt(d.nominal, 3)}</td>
                    <td data-testid={`agios-${d.id}`} className={`${td} tabular font-data text-right text-red-700`}>{fmt(d.agios, 3)}</td>
                    <td className={`${td} font-data text-xs font-bold`}>{d.echeance}</td>
                    <td className={td}>
                      <span className={`border px-1.5 py-0.5 font-data text-[10px] font-bold uppercase ${d.rembourse ? 'border-slate-300 bg-slate-100 text-slate-500' : 'border-amber-300 bg-amber-50 text-amber-900'}`}>
                        {d.rembourse ? 'Remboursé' : 'En cours'}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button type="button" data-testid={`rembourser-${d.id}`} onClick={async () => { const { error } = await rembourserDeblocage(d.id, user?.name); if (error) return toast.error(error); toast.success(d.rembourse ? 'Remboursement annulé' : `Traite remboursée — plafond reconstitué de ${fmt(d.nominal, 3)} TND`); load(); }} className="flex h-9 items-center gap-1.5 border border-slate-300 px-2.5 text-xs font-bold text-slate-700 hover:bg-slate-900 hover:text-white">
                        <Check size={13} /> {d.rembourse ? 'Annuler' : 'Rembourser'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        </>
      )}

      {onglet === 'canonique' && (
        <div data-testid="tresorerie-canonique">
          <PrevisionnelCanonique />
        </div>
      )}

      {onglet === 'echeancier' && (
        <>
          <section data-testid="charge-semaines" className="border border-slate-200 bg-white px-4 py-3">
            <p className="font-heading text-base font-bold text-slate-900">Charge de trésorerie par semaine</p>
            <div className="mt-3 space-y-1.5">
              {Object.entries(totalParSemaine).slice(0, 14).map(([s, m]) => (
                <div key={s} data-testid={`semaine-${s}`} className="flex items-center gap-3">
                  <span className="w-24 font-data text-[11px] text-slate-600">{s}</span>
                  <div className="h-4 flex-1 bg-slate-100">
                    <div className={`h-4 ${m > semaineMax * 0.66 ? 'bg-red-600' : m > semaineMax * 0.33 ? 'bg-amber-500' : 'bg-emerald-600'}`} style={{ width: `${(m / semaineMax) * 100}%` }} />
                  </div>
                  <span className="tabular w-32 text-right font-data text-xs font-semibold text-slate-900">{fmt(m, 3)} TND</span>
                </div>
              ))}
            </div>
          </section>

          <section className="border border-slate-200 bg-white">
            <p className="border-b border-slate-200 px-4 py-3 font-heading text-base font-bold text-slate-900">
              Échéancier de trésorerie global <span className="font-data text-xs text-slate-500">({echeancier.length} sortie(s) à venir)</span>
            </p>
            <table className="w-full border-collapse text-left" data-testid="table-echeancier-global">
              <thead className="bg-slate-50">
                <tr>{['Échéance', 'Semaine', 'Origine', 'Tiers / organisme', 'Référence', 'Montant (TND)'].map((c) => <th key={c} className={th}>{c}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {echeancier.length === 0 && <tr><td colSpan={6} className="px-3 py-10 text-center text-sm text-slate-500">Aucune sortie planifiée.</td></tr>}
                {echeancier.map((l) => (
                  <tr key={`${l.source}-${l.id}`} data-testid={`echeance-globale-${l.id}`} className={l.echu ? 'bg-red-50' : ''}>
                    <td className={`${td} font-data text-xs font-bold ${l.echu ? 'text-red-700' : ''}`}>
                      {l.echeance}
                      {l.echu && <AlertTriangle size={12} className="ml-1 inline text-red-600" />}
                    </td>
                    <td className={`${td} font-data text-xs text-slate-500`}>{l.semaine}</td>
                    <td className={td}>
                      <span className={`border px-1.5 py-0.5 font-data text-[10px] font-bold uppercase ${COULEUR_SOURCE[l.source] ?? 'border-slate-300 bg-slate-50'}`}>{l.source}</span>
                    </td>
                    <td className={td}>{l.tiers}</td>
                    <td className={`${td} font-data text-xs text-slate-600`}>{l.reference}</td>
                    <td className={`${td} tabular font-data text-right font-semibold`}>{fmt(l.montant, 3)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-slate-100">
                  <td className={`${th} text-left`} colSpan={5}>Total des sorties planifiées</td>
                  <td className={`${th} text-right`}>{fmt(echeancier.reduce((s, l) => s + l.montant, 0), 3)}</td>
                </tr>
              </tfoot>
            </table>
          </section>
        </>
      )}

      {complement && (
        <div data-testid="complement-dialog" className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
          <div className="w-full max-w-lg border border-slate-200 bg-white">
            <div className="border-b border-slate-200 px-5 py-4">
              <h2 className="font-heading text-lg font-black text-slate-900">Déblocage de crédit détecté</h2>
              <p className="font-data text-[11px] uppercase tracking-[0.1em] text-slate-500">
                Montant net encaissé : {fmt(complement.montantNet, 3)} TND · {complement.date}
              </p>
            </div>
            <div className="grid grid-cols-1 gap-4 px-5 py-4 sm:grid-cols-2">
              <div>
                <label className={label}>Montant nominal (dette réelle) *</label>
                <input data-testid="complement-nominal" type="number" step="0.001" min="0" value={complement.nominal} onChange={(e) => setComplement({ ...complement, nominal: e.target.value })} className={inputCls} autoFocus />
              </div>
              <div>
                <label className={label}>Date d'échéance de la traite *</label>
                <input data-testid="complement-echeance" type="date" value={complement.echeance} onChange={(e) => setComplement({ ...complement, echeance: e.target.value })} className={inputCls} />
              </div>
              <p data-testid="complement-agios" className="sm:col-span-2 border-l-4 border-red-600 bg-red-50 px-3 py-2 font-data text-xs text-red-900">
                Agios calculés automatiquement : <span className="text-sm font-bold">{fmt(Math.max(0, (Number(complement.nominal) || 0) - complement.montantNet), 3)} TND</span>{' '}
                (nominal − net). Le nominal consomme le plafond et s'inscrit à l'échéancier ; il sera reconstitué au remboursement.
              </p>
            </div>
            <div className="flex gap-3 border-t border-slate-200 px-5 py-4">
              <button
                type="button"
                data-testid="complement-valider"
                onClick={async () => {
                  const { error } = await enregistrerDeblocage({ ...complement, utilisateur: user?.name });
                  if (error) return toast.error(error);
                  toast.success('Déblocage enregistré — traite inscrite à l’échéancier');
                  setDetections((ds) => ds.filter((_, j) => j !== complement.index));
                  setComplement(null);
                  load();
                }}
                className="h-11 flex-1 bg-slate-900 font-heading text-sm font-bold uppercase tracking-[0.08em] text-white hover:bg-slate-700"
              >
                Enregistrer le déblocage
              </button>
              <button type="button" data-testid="complement-annuler" onClick={() => setComplement(null)} className="h-11 border border-slate-300 px-5 text-sm font-semibold text-slate-700 hover:bg-slate-100">
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
